import { useState } from "react";
import { useScrollAnimationMultiple } from "@/hooks/useScrollAnimation";
import { galleryImages } from "@/mocks/galleryData";

export default function GalleryGrid() {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  const filtered = galleryImages;

  const { setRef, visibleItems } = useScrollAnimationMultiple(filtered.length);

  const openLightbox = (idx: number) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);
  const prevImage = () => setLightboxIdx((prev) => (prev !== null ? (prev - 1 + filtered.length) % filtered.length : null));
  const nextImage = () => setLightboxIdx((prev) => (prev !== null ? (prev + 1) % filtered.length : null));

  return (
    <section id="gallery-grid" className="py-16 md:py-20 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-white border border-[#7FBF3F]/20 text-[#7FBF3F] text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wider">
            <i className="ri-image-2-line"></i>Project Gallery
          </div>
          <h2
            className="font-serif text-3xl md:text-4xl font-bold text-[#212121] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Our Projects in Pictures
          </h2>
          <p className="text-gray-500 text-sm md:text-base max-w-xl mx-auto">
            Real photographs from our residential developments on Sarjapur Road, Bengaluru.
          </p>
        </div>



        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((img, idx) => (
            <div
              key={img.id}
              ref={setRef(idx) as (el: HTMLDivElement | null) => void}
              onClick={() => openLightbox(idx)}
              className={`group relative overflow-hidden rounded-2xl cursor-pointer border border-gray-100 reveal reveal-delay-${Math.min(idx + 1, 6)} ${visibleItems[idx] ? "visible" : ""} ${img.size === "wide" ? "lg:col-span-2" : ""}`}
            >
              <div className={`w-full overflow-hidden ${img.size === "wide" ? "h-64 md:h-72" : "h-56 md:h-64"}`}>
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#1B2A4A]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-5">
                <div>
                  <span className="bg-[#7FBF3F]/90 text-white text-xs px-2.5 py-1 rounded-full mb-2 inline-block font-medium">
                    {img.category}
                  </span>
                  <p className="text-white font-semibold text-sm">{img.title}</p>
                  <p className="text-white/70 text-xs">{img.project}</p>
                </div>
              </div>
              <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <i className="ri-zoom-in-line text-[#1B2A4A] text-sm"></i>
              </div>
            </div>
          ))}
        </div>

        {/* Count */}
        <p className="text-center text-gray-400 text-sm mt-8">
          Showing {filtered.length} of {galleryImages.length} photos
        </p>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
            onClick={closeLightbox}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
          <div
            className="max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={filtered[lightboxIdx].src}
              alt={filtered[lightboxIdx].alt}
              className="w-full max-h-[80vh] object-contain rounded-xl"
            />
            <div className="text-center mt-4">
              <p className="text-white font-semibold">{filtered[lightboxIdx].title}</p>
              <p className="text-white/60 text-sm">{filtered[lightboxIdx].project}</p>
              <p className="text-white/40 text-xs mt-1">{lightboxIdx + 1} / {filtered.length}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
